import React from 'react';
import ChatbotWordLookup from '../components/ChatbotWordLookup';
import ChatbotKanjiLookup from '../components/ChatbotKanjiLookup';
import ChatbotChineseLookup from '../components/ChatbotChineseLookup';
import TinySegmenter from './tinySegmenter';
import logger from './logger';
import { useWordContext } from '../context/WordContext';
import apiClient from '../utils/apiClient';

// Helper for debugging
const logWordSelection = (word, context, language) => {
  logger.debug(`processChatbotSentence - Word selected: "${word}" [${language}]`);
  logger.debug(`processChatbotSentence - Context: "${context?.substring(0, 50)}..."`);
};

// Function to check if a character is a kanji
const isKanjiCharacter = (char) => {
  return /[\u4E00-\u9FFF]/.test(char);
};

// Function to check if a character is a Chinese character (Hanzi)
const isHanziCharacter = (char) => {
  return /[\u4E00-\u9FFF]/.test(char);
};

// Component to handle Japanese sentences with word styling
const JapaneseSentence = ({ sentence, onWordSelect }) => {
  // Get word context for styling
  const { 
    isWordSaved, 
    getWordGrade, 
    isWordKnown,
    isKanjiSaved,
    getKanjiGrade,
    isKanjiKnown
  } = useWordContext();
  
  // Clean up the sentence - remove any [storySentence] placeholders
  const cleanedSentence = sentence.replace(/\[storySentence\]/g, '').trim();
  
  // Use TinySegmenter to tokenize Japanese text
  const segmenter = new TinySegmenter();
  const words = segmenter.segment(cleanedSentence);
  
  // Extract sentence context for a word
  const extractSentenceContext = (fullText, targetWord) => {
    try {
      const minContextLength = 20; // minimum characters of context before and after
      
      if (!fullText || !targetWord) return fullText;
      
      const targetIndex = fullText.indexOf(targetWord);
      
      if (targetIndex === -1) return fullText;
      
      let startIndex = Math.max(0, targetIndex - minContextLength);
      let endIndex = Math.min(fullText.length, targetIndex + targetWord.length + minContextLength);
      
      // Adjust to not cut in the middle of words
      while (startIndex > 0 && !/\s/.test(fullText[startIndex])) {
        startIndex--;
      }
      
      while (endIndex < fullText.length && !/\s/.test(fullText[endIndex])) {
        endIndex++;
      }
      
      return fullText.substring(startIndex, endIndex).trim();
    } catch (err) {
      logger.error(`processChatbotSentence - Error extracting context for "${targetWord}":`, err);
      return fullText;
    }
  };
  
  // Handle word click
  const handleWordSelect = (word) => {
    // Extract context for this specific word
    const wordContext = extractSentenceContext(cleanedSentence, word);
    logWordSelection(word, wordContext, 'Japanese');
    
    // Call the provided onWordSelect function
    if (onWordSelect && typeof onWordSelect === 'function') {
      onWordSelect(word, wordContext);
    }
  };
  
  // Function to get CSS class for a word based on its saved status
  const getWordClass = (word) => {
    const isSaved = isWordSaved(word, 'Japanese');
    
    if (!isSaved) {
      return 'chatbot-clickable-word';
    }
    
    const grade = getWordGrade(word, 'Japanese') || 1;
    const known = isWordKnown(word, 'Japanese');
    
    if (known) {
      return 'chatbot-clickable-word';
    }
    
    return `chatbot-clickable-word chatbot-saved-word chatbot-grade-${grade}`;
  };
  
  // Function to get CSS class for a kanji character
  const getKanjiClass = (kanjiChar) => {
    const isSaved = isKanjiSaved(kanjiChar);
    
    if (!isSaved) {
      return 'chatbot-clickable-kanji';
    }
    
    const grade = getKanjiGrade(kanjiChar) || 1;
    const known = isKanjiKnown(kanjiChar);
    
    if (known) {
      return 'chatbot-clickable-kanji';
    }
    
    return `chatbot-clickable-kanji chatbot-saved-kanji chatbot-grade-${grade}`;
  };
  
  return (
    <span className="chatbot-japanese-sentence">
      {words.map((word, index) => {
        // Check if the word contains Japanese characters
        if (/[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]/.test(word)) {
          // Handle words with kanji characters
          if (isKanjiCharacter(word) || Array.from(word).some(isKanjiCharacter)) {
            // For words with kanji, we need to style individual kanji characters
            if (word.length > 1 && Array.from(word).some(isKanjiCharacter)) {
              return (
                <span
                  key={`word-${index}`}
                  className={getWordClass(word)}
                  onClick={() => handleWordSelect(word)}
                >
                  {Array.from(word).map((char, charIndex) => {
                    if (isKanjiCharacter(char)) {
                      return (
                        <span
                          key={`char-${charIndex}`}
                          className={getKanjiClass(char)}
                        >
                          {char}
                        </span>
                      );
                    }
                    return char;
                  })}
                </span>
              );
            } else {
              // Single kanji character or non-kanji word
              return (
                <span
                  key={`word-${index}`}
                  className={getWordClass(word)}
                  onClick={() => handleWordSelect(word)}
                >
                  {word}
                </span>
              );
            }
          } else {
            // Regular Japanese words without kanji
            return (
              <span
                key={`word-${index}`}
                className="chatbot-clickable-word"
                onClick={() => handleWordSelect(word)}
              >
                {word}
              </span>
            );
          }
        }
        // Non-Japanese text
        return <React.Fragment key={`text-${index}`}>{word}</React.Fragment>;
      })}
    </span>
  );
};

// New component to handle Chinese sentences with segmenting via backend
const ChineseSentence = ({ sentence, onWordSelect, baseLanguage }) => {
  const [segmentedSentence, setSegmentedSentence] = React.useState([]);
  const [isSegmenting, setIsSegmenting] = React.useState(true);
  
  // Get word context for styling
  const { 
    isWordSaved, 
    getWordGrade, 
    isWordKnown,
    isHanziSaved,
    getHanziGrade,
    isHanziKnown
  } = useWordContext();
  
  // Clean up the sentence - remove any [storySentence] placeholders
  const cleanedSentence = sentence.replace(/\[storySentence\]/g, '').trim();
  
  // Use backend segmentation for Chinese
  React.useEffect(() => {
    const segmentChineseSentence = async () => {
      try {
        setIsSegmenting(true);
        logger.debug('ChineseSentence - Segmenting Chinese sentence:', cleanedSentence);
        
        // Call the backend API to segment the sentence
        const response = await apiClient.post('/segment-chinese', { 
          sentence: cleanedSentence 
        });
        
        // Check if we got a valid response with segments
        if (response && response.segments && Array.isArray(response.segments)) {
          logger.debug('ChineseSentence - Segmentation result:', response.segments);
          setSegmentedSentence(response.segments);
        } else {
          // Fallback: just split every character as a separate segment
          logger.error('ChineseSentence - Invalid segmentation response, using character fallback');
          setSegmentedSentence(Array.from(cleanedSentence));
        }
      } catch (error) {
        logger.error('ChineseSentence - Error segmenting sentence:', error);
        // Fallback: just split every character as a separate segment
        setSegmentedSentence(Array.from(cleanedSentence));
      } finally {
        setIsSegmenting(false);
      }
    };
    
    if (cleanedSentence) {
      segmentChineseSentence();
    }
  }, [cleanedSentence]);
  
  // Extract sentence context for a word
  const extractSentenceContext = (fullText, targetWord) => {
    try {
      const minContextLength = 20; // minimum characters of context before and after
      
      if (!fullText || !targetWord) return fullText;
      
      const targetIndex = fullText.indexOf(targetWord);
      
      if (targetIndex === -1) return fullText;
      
      let startIndex = Math.max(0, targetIndex - minContextLength);
      let endIndex = Math.min(fullText.length, targetIndex + targetWord.length + minContextLength);
      
      // Adjust to not cut in the middle of words
      while (startIndex > 0 && !/\s/.test(fullText[startIndex])) {
        startIndex--;
      }
      
      while (endIndex < fullText.length && !/\s/.test(fullText[endIndex])) {
        endIndex++;
      }
      
      return fullText.substring(startIndex, endIndex).trim();
    } catch (err) {
      logger.error(`ChineseSentence - Error extracting context for "${targetWord}":`, err);
      return fullText;
    }
  };
  
  // Handle word click
  const handleWordSelect = (word) => {
    // Extract context for this specific word
    const wordContext = extractSentenceContext(cleanedSentence, word);
    logWordSelection(word, wordContext, 'Chinese');
    
    // Call the provided onWordSelect function
    if (onWordSelect && typeof onWordSelect === 'function') {
      onWordSelect(word, wordContext);
    }
  };
  
  // Function to get CSS class for a word based on its saved status
  const getWordClass = (word) => {
    const isSaved = isWordSaved(word, 'Chinese');
    
    if (!isSaved) {
      return 'chatbot-clickable-word';
    }
    
    const grade = getWordGrade(word, 'Chinese') || 1;
    const known = isWordKnown(word, 'Chinese');
    
    if (known) {
      return 'chatbot-clickable-word';
    }
    
    return `chatbot-clickable-word chatbot-saved-word chatbot-grade-${grade}`;
  };
  
  // Function to get CSS class for a hanzi character
  const getHanziClass = (hanziChar) => {
    const isSaved = isHanziSaved(hanziChar);
    
    if (!isSaved) {
      return 'chatbot-clickable-hanzi';
    }
    
    const grade = getHanziGrade(hanziChar) || 1;
    const known = isHanziKnown(hanziChar);
    
    if (known) {
      return 'chatbot-clickable-hanzi';
    }
    
    return `chatbot-clickable-hanzi chatbot-saved-hanzi chatbot-grade-${grade}`;
  };
  
  if (isSegmenting) {
    return <span className="chatbot-chinese-sentence">Loading...</span>;
  }
  
  return (
    <span className="chatbot-chinese-sentence">
      {segmentedSentence.map((word, index) => {
        // Check if the word contains Chinese characters
        if (/[\u4E00-\u9FFF]/.test(word)) {
          return (
            <ChatbotChineseLookup
              key={`word-${index}`}
              word={word}
              sentence={cleanedSentence}
              onSelectWord={onWordSelect || handleWordSelect}
              baseLanguage={baseLanguage}
            />
          );
        }
        // Non-Chinese text (punctuation, etc.)
        return <React.Fragment key={`text-${index}`}>{word}</React.Fragment>;
      })}
    </span>
  );
};

const processChatbotSentence = (input, language, onSelectWord, baseLanguage) => {
  let sentence = input;
  
  // Check for invalid input
  if (typeof sentence !== 'string') {
    if (sentence && typeof sentence.targetSentence === 'string') {
      sentence = sentence.targetSentence;
    } else {
      return sentence;
    }
  }

  // For Japanese, use the JapaneseSentence component
  if (language === 'Japanese') {
    // If there are no Japanese characters, return the sentence as-is.
    if (!/[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]/.test(sentence)) {
      return sentence;
    }

    // Store the full sentence in a global variable for context lookups
    window.currentChatSentence = sentence;
    
    return <JapaneseSentence sentence={sentence} onWordSelect={onSelectWord} />;
  }
  
  // For Chinese, use the ChineseSentence component
  if (language === 'Chinese') {
    // If there are no Chinese characters, return the sentence as-is
    if (!/[\u4E00-\u9FFF]/.test(sentence)) {
      return sentence;
    }
    
    // Store the full sentence in a global variable for context lookups
    window.currentChatSentence = sentence;
    
      return <ChineseSentence sentence={sentence} onWordSelect={onSelectWord} baseLanguage={baseLanguage} />;
  }

  // For all other languages, we'll directly render the text with proper handling
  // of words, punctuation, and apostrophes
  const elements = [];
  
  // First, let's preprocess the text to ensure punctuation has no spaces before it
  // This ensures things like "plaisent !" become "plaisent!"
  const preprocessedText = sentence.replace(/\s+([.,;:!?()[\]{}"])/g, "$1");
  
  // Helper function to process a piece of text, to keep the words clickable even in quotes
  const processTextPiece = (text, startIndex) => {
    // Split the text into words and spaces
    const words = text.split(/(\s+)/);
    const result = [];
    
    words.forEach((word, wordIndex) => {
      // Skip spaces, keep them as-is
      if (/^\s+$/.test(word)) {
        result.push(<React.Fragment key={`space-${startIndex + wordIndex}`}>{word}</React.Fragment>);
        return;
      }
      
      // Split the word if it has punctuation attached at the end
      // This regex captures: 1) the word itself, 2) any punctuation at the end
      const match = word.match(/^(.+?)([.,;:!?")}\]]*)$/);
      
      if (match) {
        const actualWord = match[1];
        const punctuation = match[2];
        
        // Handle apostrophes inside words (don't split around them)
        if (actualWord.includes("'") && !language.includes('Chinese') && !language.includes('Japanese')) {
          // Use ChatbotWordLookup for the word
          result.push(
            <ChatbotWordLookup
              key={`word-${startIndex + wordIndex}`}
              word={actualWord}
              language={language}
              sentence={sentence}
              onSelectWord={onSelectWord}
              baseLanguage={baseLanguage}
            />
          );
        } else {
          // Use ChatbotWordLookup for the word
          result.push(
            <ChatbotWordLookup
              key={`word-${startIndex + wordIndex}`}
              word={actualWord}
              language={language}
              sentence={sentence}
              onSelectWord={onSelectWord}
              baseLanguage={baseLanguage}
            />
          );
        }
        
        // Add punctuation as-is
        if (punctuation) {
          result.push(<React.Fragment key={`punct-${startIndex + wordIndex}`}>{punctuation}</React.Fragment>);
        }
      } else {
        // Simple word with no punctuation
        result.push(
          <ChatbotWordLookup
            key={`word-${startIndex + wordIndex}`}
            word={word}
            language={language}
            sentence={sentence}
            onSelectWord={onSelectWord}
            baseLanguage={baseLanguage}
          />
        );
      }
    });
    
    return result;
  };
  
  // We'll need to handle quotes specially to maintain the text structure
  let currentText = '';
  let startIndex = 0;
  
  for (let i = 0; i < preprocessedText.length; i++) {
    const char = preprocessedText[i];
    
    // Process accumulated text when we hit an opening quote
    if (char === '"' || char === '"' || char === '"') {
      // Process any accumulated text before the quote
      if (currentText) {
        elements.push(...processTextPiece(currentText, startIndex));
        currentText = '';
        startIndex = i + 1; // Update start index for the next piece
      }
      
      // Add the opening quote as-is
      elements.push(<React.Fragment key={`quote-open-${i}`}>{char}</React.Fragment>);
      
      // Find the closing quote
      const restOfText = preprocessedText.slice(i + 1);
      const closingQuoteMatch = restOfText.match(/"|}|"/);
      
      if (closingQuoteMatch) {
        const quotedText = restOfText.slice(0, closingQuoteMatch.index);
        
        // Process the text within quotes
        elements.push(...processTextPiece(quotedText, i + 1));
        
        // Add the closing quote
        elements.push(
          <React.Fragment key={`quote-close-${i + 1 + closingQuoteMatch.index}`}>
            {restOfText[closingQuoteMatch.index]}
          </React.Fragment>
        );
        
        // Update position and continue
        i += 1 + closingQuoteMatch.index;
        startIndex = i + 1;
      } else {
        // No closing quote found, just add the quote and continue
        currentText = '';
        startIndex = i + 1;
      }
    } else {
      // Accumulate regular text
      currentText += char;
    }
  }
  
  // Process any remaining text
  if (currentText) {
    elements.push(...processTextPiece(currentText, startIndex));
  }
  
  return <span className="chatbot-sentence">{elements}</span>;
};

export default processChatbotSentence; 